/*global define, require*/
/*jshint devel:true*/
/**
 * Base Backbone model for this project
 * This is based on the Backbone Deep Model extension.
 * See https://github.com/powmedia/backbone-deep-model#readme for more info
 */
define(
	'BaseModel',[
		'underscore',
		'jquery',
		'backbone',
		'BaseClassGenerator',
		'BackboneValidation',
		'setupBackboneValidation',
		'jsonClone'
	],
	function (_, $, Backbone, BaseClassGenerator, BackboneValidation) {
		'use strict';

		var ParentClass = Backbone.Model,
			parentClassPrototype = ParentClass.prototype;

		/**
		 * Runs a given callback function over all leaf property of an object.
		 * Similar to map, but recursive
		 * @inner
		 * @param {*} data
		 * @param {Function} callback The function parameter is the current value of the property
		 * @returns {*}
		 */
		var recursiveMap = function (data, callback) {
			if (data && (data instanceof Object)) {
				_.each(data, function (item, index) {
					data[index] = recursiveMap(item, callback);
				});
			} else {
				data = callback(data);
			}

			return data;
		};

		/**
		 * Base Model class that provides many support methods for fetching/saving, data structure and validation
		 * @class
		 * @extends {Backbone.Model}
		 */
		var BaseModel = BaseClassGenerator.generate(ParentClass,
			/**
			 * @lends BaseModel.prototype
			 */
			{ // base model definition
				className: 'BaseModel',

				/**
				 * Ajax: request timeout delay (ms)
				 * @type {Number}
				 */
				ajaxTimeout: 90000,

				/**
				 * Ajax: expected data type
				 */
				ajaxDataType: 'json',

				/**
				 * Data type to send
				 */
				ajaxContentType: 'application/json',

				/**
				 * Reference to the BaseCollection constructor
				 * It'll be set by the BaseCollection' requirejs module
				 * @type {BaseCollection#constructor}
				 * @private
				 */
				_refBaseCollectionClass: null,

				/**
				 * Default model structure inspired from the portal data model.
				 * Unnecessary data model variables are commented out.
				 */
				defaults: {
					/**
					 * URL to create a new record on the remote server
					 * To be used by .save()
					 * @type {String}
					 */
					//createUrl: null,

					/**
					 * URL to retrieve the current data model from the remote server
					 * To be used by .fetch()
					 * @type {String}
					 */
					//readUrl: null,

					/**
					 * URL to update (or create by default) a record on the remote server.
					 * To be used by .save()
					 * @type {String}
					 */
					//updateUrl: null,

					/**
					 * URL to delete an existing record on the remote server.
					 * To be used by .destroy()
					 * @type {String}
					 */
					//deleteUrl: null
				}, // end of defaults

				/**
				 * Move CRUD URLs attributes (e.g. readUrl, updateUrl, etc...) to the object's internal properties.
				 *
				 *  @example
				 *  var model = BaseModel({
				*		readUrl: "#readUrl",
				*		updateUrl: "#updateUrl",
				*		sponge: "bob"
				*	})
				 *
				 *  // after processing by the initialize() method (that calls this method), we get:
				 *  model.attributes = { sponge: "bob" };
				 *  model.readUrl === "#readUrl";
				 *  model.updateUrl === "#updateUrl";
				 *
				 *  @return {BaseModel}
				 */
				// Disable this because we're going to leave these URLs in the model
//				moveModelUrlsToSelf: function() {
//					var self = this,
//						methodNames = 'create,read,update,delete'.split(','),
//						attributes = self.attributes;
//
//					// for each http method name
//					for(var i=0, methodNamesLen = methodNames.length, methodName, attrNameForSelf, attrNameForModel, isAttrDefined; i < methodNamesLen; i++) {
//						methodName = methodNames[i];
//						attrNameForSelf = methodName + 'Url';
//						attrNameForModel = '_' + attrNameForSelf;
//						isAttrDefined = attrNameForModel in attributes;
//
//						if (isAttrDefined) {
//							self[attrNameForSelf] = attributes[attrNameForModel];
//
//							//DEBUG
//							//console.log('removing %o', attrNameForModel);
//
//							delete attributes[attrNameForModel]; // unset that attribute from the data model
//						}
//					}
//
//					return self;
//				},

				/**
				 * Hash of attribute names to coerce to a specific type.
				 * The hash key represents the attribute name
				 * The hash value represents the attribute target type.
				 *  Some types like boolean, number and string can be coerced easily.
				 *  For Array and Object, it will just raise an error if the given value is not an array/object.
				 *
				 * @example
				 *  _coerceAttributeTypes: {
				 *    directoryListingStatus: true, // converts value to boolean
				 *    missedCallSettingStatus: 0, // convert value to number
				 *    callerIdStatus: 'a', // convert value to string
				 *    errorList: [], // throw an error if the value is not an array
				 *    dataMap: {} // throw an error if the value is not an object
				 *  }
				 * @type {Object}
				 */
				_coerceAttributeTypes: {},

				/**
				 * Setup the _coerceAttributeTypes before we start using setting the model object up.
				 * @param {Object} initModel
				 */
				constructor: function (initModel) {
					var self = this,
						_coerceAttributeTypes = initModel && initModel._coerceAttributeTypes;

					//DEBUG
					//console.warn(self.report('constructor', ''), initModel);

					// move the _coerceAttributeTypes to the object directly
					if (_coerceAttributeTypes) {
						self._coerceAttributeTypes = $.extend({}, self._coerceAttributeTypes || {}, _coerceAttributeTypes);
						delete initModel._coerceAttributeTypes;
					}

					ParentClass.apply(self, [].slice.call(arguments));
				},

				/**
				 * DEBUG
				 */
//				initialize: function() {
//					// DEBUG
//					// report all model events
//					this.on('all', function(){
//						console.warn(this, arguments);
//					});
//					parentClassPrototype.initialize.apply(this, arguments);
//				},

				/**
				 * Add ability to convert some specific data model variables to a specified type when setting them with .set().
				 * It's important for data model consistency.
				 * @note This is not an alternative to data model validation!
				 *
				 * @param {Object} attribs
				 * @param {Object} options
				 * @return {BaseModel}
				 */
				set: function (attribs, options) {
					var self = this,
						methodName = 'set',
						args = arguments,
						_coerceAttributeTypes = self._coerceAttributeTypes;

					//DEBUG
					//console.group('set: attribs = %o, options = %o', attribs, options);
					//console.group(methodName);

					if (typeof(attribs) === 'string') { // if the first argument is a property name
						if (args.length > 1) { // and we have a value to set
							var tmp = {}; // wrap the function arguments into an object
							tmp[attribs] = options;
							options = args[2];
							attribs = tmp;
						} else { // if the first argument of set() is not a plain object, then the 2nd argument is mandatory
							throw new Error(self.report(methodName, 'if the first argument of set() is not a plain object, then the 2nd argument is mandatory'));
						}
					}

					$.each(attribs, function (key, value) {

						//DEBUG
						//console.log('%o set(attr: %o = %o)', self, key, value);

						// convert to target value if required
						if (key in _coerceAttributeTypes) {

							//DEBUG
							//console.log('key in _coerceAttributeTypes = ', key in _coerceAttributeTypes);

							var referenceValue = _coerceAttributeTypes[key],
								referenceValueType = typeof referenceValue,
								isReferenceValueUndefined = (referenceValueType === 'undefined') || (referenceValue === null) || ((referenceValueType === 'number') && isNaN(referenceValue)),
								valueType = typeof value,
								newVal;

							if (isReferenceValueUndefined) {
								throw new Error(self.report(methodName, 'Unable to determine what type should be respected by the attribute "' + key + '".'));
							}

							switch (referenceValueType) {
							case 'object':
								var referenceValueIsArray = $.isArray(referenceValue);
								if ((referenceValueIsArray && !$.isArray(value)) || (!referenceValueIsArray && (valueType !== 'object'))) {
									throw new Error(self.report(methodName, 'Given value for attribute "' + key + '" should have been an ' + (referenceValueIsArray ? 'array' : 'object')));
								}
								break;
							case 'boolean':
								newVal = (valueType === 'boolean') ? value : _.toBoolean(value);
								break;
							case 'number':
								newVal = +value;

								if (isNaN(newVal)) {
									throw new Error(self.report(methodName, 'Given value for attribute "' + key + '" could not be converted to a number.'));
								}
								break;
							case 'string':
								newVal = value + '';
								break;
							default:
								throw new Error(self.report(methodName, 'type of attribute "' + key + '" is not supported.'));
							}

							attribs[key] = newVal;

							//DEBUG
							//console.log(self.report(methodName, 'convert %o, with value = %o to %o'), key, value, attribs[key]);
						}
					});

					var ret = parentClassPrototype.set.call(self, attribs, options);

					//DEBUG
					//console.groupEnd();

					return ret;
				}, // end of set: function (attribs, options)

				/**
				 * Validates a given nested model attribute.
				 * Works for Backbone models and collections.
				 * Stores this method result in this.nestedValidationError (similar to .validate() )
				 * @param {Object} [options]
				 * @returns {Object|undefined} Returns undefined when model is valid. Returns an error obecjt otherwise.
				 * Error object format:
				 * {
				 *  attrName1: errorResult1,
				 *  attrName2: errorResult2,
				 *  ...
				 * }
				 *
				 * errorResult is either a string of the error message, or a more complex object.
				 * It depends on what the field's validation function returned.
				 */
				nestedValidate: function (options) {
					var self = this,
						methodName = 'nestedValidate',
						hasError = false,
						attr = self.attributes,
						ret = {};

					if (!options) {
						options = {};
					}

					//DEBUG
					//console.group(self.report(methodName, ' this=%o args=%o'), self, arguments);

					$.each(attr, function (key, value) {
						var invalid,
							isNormalObject = false;

						//DEBUG
						//console.log(self.report(methodName, ' key=%o value=%o'), key, value);

						if ((value instanceof BaseModel) || (self._refBaseCollectionClass && (value instanceof self._refBaseCollectionClass))) { // if it's a BaseModel or BaseCollection
							invalid = value.nestedValidate(options);
						} else if (value instanceof Backbone.Model) { // if it's a Backbone model
							invalid = value.validate({}, options);
						} else { // or a normal object/data type
							var temp = {};
							temp[key] = value;
							invalid = self.validate(temp, options); // use the normal backbone-validation; it'll ignore the nested model/collection objects
							isNormalObject = true;
						}

						if (invalid) {

							//DEBUG
							//console.log(self.report(methodName, ' invalid=%o'), invalid);

							if (isNormalObject) {
								$.extend(ret, invalid);
							} else {
								ret[key] = invalid;
							}

							hasError = true;
						}
					});

					ret = hasError ? ret : undefined;

					//DEBUG
					//console.log(self.report(methodName, 'ret = %o'), ret);
					//console.groupEnd();

					self.nestedValidationError = ret; // stores this method result too
					return ret;
				},

				/**
				 * Runs nestedValidate() and returns true if the model (and nested values) are valid.
				 * @param {Object} [options]
				 * @returns {Boolean}
				 */
				isNestedValid: function (options) {
					var self = this,
						methodName = 'isNestedValid',
						ret = typeof self.nestedValidate(options) === 'undefined';

					//DEBUG
					//console.log(self.report(methodName, 'ret = %o'), ret);

					return ret;
				},

				/**
				 * Handle the read/save interactions with the remote server
				 * @note The remote URL is obtained from the object property <httpMethod> + Url.
				 * e.g. httpMethod=read -> this.readUrl
				 * @param {String} httpMethod the CRUD method ("create", "read", "update", or "delete")
				 * @param {Backbone.Model} model the model to be saved (or collection to be read)
				 * @param {Object} options success and error callbacks, and all other jQuery request options
				 */
				sync: function (httpMethod, model, options) {
					var self = this,
						methodName = 'sync',
						url = self.get(httpMethod + 'Url'),
						errorMsg;

					//DEBUG
					console.log(self.report(methodName, 'httpMethod = %o, obj = %o'), httpMethod, self);

					if (!url) {
						errorMsg = self.report(methodName, 'Unable to find remote URL for current model and httpMethod="' + httpMethod + '".');
						throw new Error(errorMsg);
					}

					return parentClassPrototype.sync.call(
						self,
						httpMethod,
						model,
						$.extend(
							{
								url: url,
								type: httpMethod === 'read' ? 'GET' : 'POST', // http method to use for outbound request
								timeout: self.ajaxTimeout,
								dataType: self.ajaxDataType, // expected data type
								contentType: self.ajaxContentType
							},
							options
						)
					);
				}, // end of sync: function(method, model, options)

				/**
				 * Extend the fetch method of Backbone
				 * Resets the model's state from the server by delegating to Backbone.sync.
				 * Useful if the model has never been populated with data, or if you'd like to ensure that you have the latest
				 * server state. A "change" event will be triggered if the server's state differs from the current attributes.
				 * Accepts success and error callbacks in the options hash, which are both passed (model, response, options) as arguments.
				 * @param options
				 * @return {jqXHR} jQuery XHR object
				 */
				fetch: function (options) {
					var self = this,
						methodName = 'fetch',
						augmentedOptions = {},
						prevSuccessHandler = options && options.success,
						prevErrorHandler = options && options.error;

					//DEBUG
					console.log(self.report(methodName, 'initial options = '), options);

					$.extend(augmentedOptions,
						options,
						{
							success: function () {
								//DEBUG
								console.log(self.report(methodName, 'success: data model fetched'), self, arguments);

								if ($.isFunction(prevSuccessHandler)) {
									prevSuccessHandler.apply(this, arguments);
								}
							},
							error: function () {
								//DEBUG
								console.error(self.report(methodName, 'error: '), self, arguments);

								if ($.isFunction(prevSuccessHandler)) {
									prevErrorHandler.apply(this, arguments);
								}
							}
						}
					);

					return parentClassPrototype.fetch.call(self, augmentedOptions);
				}, // end of fetch: function (options)

				/**
				 * @see BaseModel.nestedGetModel
				 * @param {string} nestedKeyPath List of nested attribute names, separated by '.'
				 * @returns {*|{keypath: *, model: *}}
				 */
				nestedGetModel: function(nestedKeyPath) {
					return BaseModel.nestedGetModel(this, nestedKeyPath);
				},

				/**
				 * Retrieves the attribute value of a nested model using a nested keypath
				 * @param {string} nestedKeyPath List of nested attribute names, separated by '.'
				 * @returns {*}
				 */
				nestedGet: function(nestedKeyPath) {
					var directModel = this.nestedGetModel(nestedKeyPath);
					if (!directModel || !directModel.model) {
						throw new Error(this.report('nestedGet', 'unable to find direct model'));
					}
					return directModel.model.get(directModel.keypath);
				},

				/**
				 * See Backbone.Model's parse() method on the official website.
				 * @param {Object} data
				 * @returns {Object}
				 */
				parse: function (data) {
					return recursiveMap(data, function (value) {
						if (typeof(value) !== 'string') {
							return value;
						}

						var matched = value.match(/^\/Date\((\d+)\)\/$/);
						if (matched) {
							value = new Date(+matched[1]);
							//DEBUG
							//console.warn('new value = ', value);
						}
						//DEBUG
						//console.log("value = " + value);
						return value;
					});
				},

				/**
				 * For us, it will make sure we return Date objects in the ODATA format "/Date(epochTime)/".
				 *
				 * Return a copy of the model's attributes for JSON stringification.
				 * This can be used for persistence, serialization, or for augmentation before being sent to the server.
				 * The name of this method is a bit confusing, as it doesn't actually return a JSON string -
				 * but I'm afraid that it's the way that the JavaScript API for JSON.stringify works.
				 * @see {@linkplain http://backbonejs.org/}
				 * @return {string}
				 */
				toJSON: function () {
					var self = this,
						wasReplaced = false,
						plainObject, ret;

					plainObject = parentClassPrototype.toJSON.apply(self, arguments); // any inner Date objects will be using our custom algorithm

					// replace the Date.prototype.toJSON with a custom one
					if (Date.prototype.toJSON === BaseModel._orgDateToJSON) {
						//DEBUG
						//console.log('replace the toJSON() of Date');
						Date.prototype.toJSON = BaseModel._modDateToJSON;
						wasReplaced = true;
					}

					ret = _.jsonClone(plainObject); // make a clone of our data

					// restores the Date.prototype.toJSON to the original one
					if (wasReplaced && (Date.prototype.toJSON === BaseModel._modDateToJSON)) {
						//DEBUG
						//console.log('restore the original toJSON() of Date');
						Date.prototype.toJSON = BaseModel._orgDateToJSON;
					}
					return ret;
				}
			}
		);

		/**
		 * Stores the original Date.prototype.toJSON method
		 * @type {Function}
		 * @private
		 */
		BaseModel._orgDateToJSON = Date.prototype.toJSON;

		/**
		 * Modified Date.prototype.toJSON method to follow ODATA Date string format
		 * @type {Function}
		 * @returns {string}
		 * @private
		 */
		BaseModel._modDateToJSON = function () {
			//DEBUG
			console.log('custom toJSON');
			return '/Date(' + this.getTime() + ')/';

		};

		/**
		 * Get the nested model of a chain of backbone models from a given keypath
		 * @param {Backbone.Model} startModel
		 * @param {string} nestedKeyPath List of nested attribute names, separated by '.'
		 * @returns {{keypath: string, model: BaseModel}} Returns an object containing the direct model and the direct keypath to use to get what you wanted.
		 */
		BaseModel.nestedGetModel = function(startModel, nestedKeyPath) {
			var keyPathParts = nestedKeyPath.split('.'),
				directKeyPath,
				directModel;
			if (keyPathParts.length > 1) {
				directKeyPath = keyPathParts.pop();
				directModel = _.reduce(keyPathParts, function (curModel, curKeyPath) {
					var nextModel;

					if (curModel instanceof ParentClass) { // enforce the fact that we're working with BaseModel instances only
						nextModel = curModel.get(curKeyPath);
					} else {
						nextModel = null;
					}
					//DEBUG
					//console.warn('subscribe: curModel=%o, curKeyPath=%o, nextModel=%o', curModel, curKeyPath, nextModel);

					return nextModel;
				}, startModel);
			} else {
				directKeyPath = nestedKeyPath;
				directModel = startModel;
			}
			return {
				keypath: directKeyPath,
				model: directModel
			};
		};

		// mixin the Backbone-Validation plugin on the base model
		$.extend(BaseModel.prototype, BackboneValidation.mixin);

		return BaseModel;
	}
);
